import { Box, Chip, FormHelperText, Stack, TextField, Typography } from '@mui/material'
import { useState, type KeyboardEvent } from 'react'

type Props = {
  label?: string
  aliases: string[]
  onChange: (aliases: string[]) => void
  placeholder?: string
  disabled?: boolean
}

export function AliasTagInput({ label = 'Aliases', aliases, onChange, placeholder, disabled }: Props) {
  const [draft, setDraft] = useState('')

  const addAliases = (raw: string) => {
    const parts = raw
      .split(/[\n,;]+/)
      .map((p) => p.trim())
      .filter(Boolean)
    if (parts.length === 0) return
    const next = [...aliases]
    for (const part of parts) {
      if (next.some((a) => a.toLowerCase() === part.toLowerCase())) continue
      next.push(part)
    }
    onChange(next)
    setDraft('')
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addAliases(draft)
    }
    if (e.key === 'Backspace' && !draft && aliases.length > 0) {
      onChange(aliases.slice(0, -1))
    }
  }

  return (
    <Box>
      <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      {aliases.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1 }}>
          {aliases.map((a) => (
            <Chip
              key={a}
              size="small"
              label={a}
              disabled={disabled}
              onDelete={disabled ? undefined : () => onChange(aliases.filter((x) => x !== a))}
            />
          ))}
        </Stack>
      )}
      <TextField
        size="small"
        fullWidth
        disabled={disabled}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => addAliases(draft)}
        placeholder={aliases.length === 0 ? placeholder ?? 'Type alias, press Enter' : 'Add another…'}
        onPaste={(e) => {
          const text = e.clipboardData.getData('text')
          if (text.includes(',') || text.includes('\n')) {
            e.preventDefault()
            addAliases(text)
          }
        }}
      />
      <FormHelperText>Separate aliases with comma or Enter</FormHelperText>
    </Box>
  )
}
